import styled from 'styled-components';

import NotificationStyled from '../../../../../components/Notification';
import StickyNav from '../../../../../components/StickyNav';
import { IconInfoCircle as InfoCircle } from '../../../../../core';
import UseCasePalettes from '../../../components/UseCasePalettes';

interface Props {
  className?: string;
}

const UseCaseTab = ({ className = '' }: Props) => (
  <div className={className}>
    <div className="tab-content">
      <div className="definition">
        <h2 className="title" id="definition">
          Definition
        </h2>
        <p>
          A vertical stacked lollipop chart is a variation of the stacked bar chart where each
          category is represented by a vertical line with circular markers stacked along it. Each
          marker represents a subcategory&apos;s value, and the total height of the stack shows the
          cumulative value. This chart is useful for visualizing the distribution of multiple
          components within each category while highlighting the total value and individual
          contributions.
        </p>
      </div>
      <div className="usage">
        <h2 className="title" id="usage">
          Usage
        </h2>
        <p className="subtitle">Vertical stacked lollipop charts are best suited for:</p>
        <ul className="ordered-list-badge-num">
          <li>
            <strong>Visualizing Proportional Contributions:</strong> They display how individual
            components contribute to a total within each category, such as showing the breakdown of
            expenses in a budget.
          </li>
          <li>
            <strong>Highlighting Category Totals:</strong> The stacked structure emphasizes overall
            values while allowing viewers to see the relative size of each component within the
            total.
          </li>
          <li>
            <strong>Depicting Cumulative Data Trends:</strong> Vertical stacked lollipop charts are
            useful for illustrating cumulative effects over categories, such as aggregated sales
            across multiple product lines.
          </li>
        </ul>
      </div>
      <div className="limitations">
        <h2 className="title" id="limitations">
          Limitations
        </h2>
        <p className="subtitle">
          Vertical stacked lollipop charts provide a visually appealing way to represent
          hierarchical or proportional data, but they come with certain limitations that should be
          considered:
        </p>
        <ul className="ordered-list-badge-num">
          <li>
            <strong>Difficulty in Comparing Subcategory Heights:</strong> The stacked format makes
            it challenging to compare individual subcategories between stacks, as their positions
            and sizes can be harder to distinguish visually.
          </li>
          <li>
            <strong>Limited Effectiveness for Large Data Sets:</strong> With many stacks or
            subcategories, the chart can become cluttered, reducing readability and making it harder
            to focus on key insights.
          </li>
          <li>
            <strong>Precision Issues for Small Subcategories:</strong> Small subcategories within a
            stack may not stand out clearly, making it difficult to analyze finer details or small
            variations accurately.
          </li>
        </ul>
      </div>
      <div className="colors">
        <h2 className="title" id="colors">
          Colors
        </h2>
        <p>
          For vertical stacked lollipop charts, color selection is crucial to differentiate between
          the various segments within each stack. Distinct, contrasting colors make it easier to
          identify the individual components, while subtle gradients can represent variations or
          trends. Careful color choices improve readability and help highlight key data points
          without causing confusion.
        </p>
        <NotificationStyled
          icon={<InfoCircle />}
          title="For in-depth information on this topic, please refer to the Colors page."
          color="blue"
          className="info-notification"
        />
        <UseCasePalettes palettes={['qualitative', 'sequential', 'diverging']} />
      </div>

      <div className="challenges">
        <h2 className="title">Challenges</h2>
        <p>
          Vertical stacked lollipop charts provide an innovative way to display data composition and
          overall trends, but they come with unique challenges:
        </p>
        <ul className="ordered-list-badge-num">
          <li>
            <strong>Complex Subcategory Comparisons:</strong> Comparing individual subcategories is
            challenging since they are stacked, making it harder to gauge precise differences.
          </li>
          <li>
            <strong>Overwhelming with Many Stacks:</strong> As the number of subcategories
            increases, the chart can become visually overwhelming, reducing interpretability.
          </li>
          <li>
            <strong>Visual Distortion Risks:</strong> The alignment of lollipops may exaggerate or
            underplay differences, potentially leading to misinterpretation of data proportions.
          </li>
        </ul>
      </div>
    </div>
    <div>
      <StickyNav title="Contents" />
    </div>
  </div>
);

const StyledUseCaseTab = styled(UseCaseTab)`
  display: flex;
  gap: 2.5rem;
  justify-content: space-between;

  > .tab-content {
    width: 100%;
    .usage,
    .definition,
    .limitations,
    .challenges {
      max-width: 700px;
    }
    > .colors {
      > .info-notification {
        margin-top: 24px;
        margin-bottom: 40px;
      }

      > .palette {
        > .palette-title {
          color: ${({ theme }) => theme.neutralText};
          font-size: ${({ theme }) => theme.fontSizes.base};
          line-height: ${({ theme }) => theme.lineHeights.base};
          margin-bottom: 4px;
        }
      }

      .color-palette {
        margin-top: 12px;
      }
    }

    > .challenges {
      > .ordered-list-badge-num {
        margin-top: 16px;
      }
    }

    .title {
      color: ${({ theme }) => theme.neutralText};
      font-weight: 600;
      font-size: ${({ theme }) => theme.fontSizes.lg};
      line-height: ${({ theme }) => theme.lineHeights.lg};
      margin-bottom: 8px;
      margin-top: 40px;
    }

    p {
      color: ${({ theme }) => theme.neutralTextWeak};
      font-size: ${({ theme }) => theme.fontSizes.sm};
      line-height: ${({ theme }) => theme.lineHeights.sm};
      font-weight: 400;
      max-width: 700px;
    }

    .subtitle {
      margin-bottom: 16px;
    }

    // styles for list with badge
    .ordered-list-badge-num {
      font-size: 0.875rem;
      line-height: 1.4285714286;
      margin-left: 29px;
      counter-reset: num;
      list-style: none;
      color: ${({ theme }) => theme.neutralTextWeak};

      li {
        position: relative;
        margin-bottom: 16px;

        &::before {
          content: counter(num);
          counter-increment: num;
          position: absolute;
          left: -1.75rem;
          top: 0;
          width: 1.25rem;
          height: 1.25rem;
          border-radius: 0.1875rem;
          text-align: center;
          font-size: 0.6875rem;
          line-height: 1.8;
          color: ${({ theme }) => theme.primaryText};
          background-color: ${({ theme }) => theme.primaryBackgroundActive};
        }
      }
    }
  }
`;

export default StyledUseCaseTab;
